import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["options"];
import { Fragment, useState } from 'react';
import fuzzy from 'fuzzy';
import FormattedMessage from 'I18n/components/FormattedMessage';
import H5 from 'UIComponents/elements/headings/H5';
import H7 from 'UIComponents/elements/headings/H7';
import UISearchInput from 'UIComponents/input/UISearchInput';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIHelpIcon from 'UIComponents/icon/UIHelpIcon';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { FLINT } from 'HubStyleTokens/colors';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { useUpgradeModal } from 'ui-addon-upgrades/ums/useUpgradeModal';
import { TriggerCategories } from 'automation-ui-enrollment/constants/TriggerCategories';
import { WEBHOOK_EVENTS_GROUP } from 'automation-ui-enrollment/constants/TriggerGroups';
import CategoryAccordion from 'automation-ui-action-features/action-panel/CategoryAccordion';
import CategoryItem from 'automation-ui-action-features/action-panel/CategoryItem';
import ActionIcon from 'automation-ui-flow-core/canvas/ActionIcon';
import { StyledQuickActionContainer, QuickAction } from 'automation-ui-canvas/action-features/action-panel/QuickActionStyledComponents';
import { getScheduledTriggerOption, getManualTriggerOption } from 'automation-ui-canvas/create/constants/TriggerOptions';
import useWorkflowTypePQLs from 'automation-ui-enrollment/components/workflowType/hooks/useWorkflowTypePQLs';
import { useHasScope } from 'automation-ui-core/hooks/authHooks';
import { WEBHOOK_TRIGGERS_ACCESS } from 'automation-ui-enrollment/constants/Scopes';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { Breadcrumbs } from 'foundations-components/transitional/components/Breadcrumbs';
import { Link } from 'foundations-components/transitional/components/Link';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export default function TriggerSelectionPanel({
  triggerOptions,
  triggerOptionsLoading,
  onSelectTriggerOption
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const manualTriggerOption = getManualTriggerOption();
  const scheduledTriggerOption = getScheduledTriggerOption();
  const {
    options: workflowTypeOptions,
    loading: pqlsLoading,
    isLocked,
    shouldSelectObjectType,
    CustomModal,
    onCloseCustomModal
  } = useWorkflowTypePQLs();
  const workflowTypeSearchResults = searchTerm.length === 0 ? workflowTypeOptions : fuzzy.filter(searchTerm, workflowTypeOptions, {
    extract: ({
      text
    }) => text
  }).map(({
    original
  }) => original);
  const triggerOptionsSearchResults = searchTerm.length === 0 ? triggerOptions : triggerOptions.map(category => Object.assign({}, category, {
    options: category.options.map(_ref => {
      let {
          options
        } = _ref,
        rest = _objectWithoutPropertiesLoose(_ref, _excluded);
      return Object.assign({}, rest, {
        options: fuzzy.filter(searchTerm, options, {
          extract: option => {
            var _option$description;
            return `${option.text} ${(_option$description = option.description) !== null && _option$description !== void 0 ? _option$description : ''} ${workflowTypeSearchResults.filter(({
              value
            }) => option.objectTypeIds.has(value)).map(({
              text
            }) => text).join(' ')}`;
          }
        }).sort(({
          score: scoreA
        }, {
          score: scoreB
        }) => scoreB - scoreA).map(({
          original
        }) => original)
      });
    }).filter(({
      options
    }) => {
      return options.length > 0;
    })
  })).filter(({
    options
  }) => {
    return options.length > 0;
  });
  const hasWebhookTriggersScope = useHasScope(WEBHOOK_TRIGGERS_ACCESS);
  const {
    openUpgradeModal: openWebhookUpgradeModal
  } = useUpgradeModal('webhook-triggers-selection-panel', !hasWebhookTriggersScope);
  const isTriggerOptionLocked = triggerOption => {
    if (triggerOption.type === 'EVENT') {
      const [objectTypeId] = triggerOption.supportedObjectTypeIds;
      if (objectTypeId && isLocked(objectTypeId)) {
        return true;
      }
    } else if (triggerOption.type === 'WEBHOOK') {
      return !hasWebhookTriggersScope;
    }
    return false;
  };
  const handleSelectTriggerOption = triggerOption => {
    if (triggerOption.type === 'EVENT') {
      const [objectTypeId] = triggerOption.supportedObjectTypeIds;
      if (objectTypeId && !shouldSelectObjectType(objectTypeId)) {
        return;
      }
    } else if (triggerOption.type === 'WEBHOOK' && !hasWebhookTriggersScope) {
      openWebhookUpgradeModal();
      return;
    }
    onSelectTriggerOption(triggerOption);
  };
  let content = null;
  if (selectedGroupId) {
    let selectedGroup;
    triggerOptions.some(({
      options
    }) => options.some(group => {
      if (group.groupId === selectedGroupId) {
        selectedGroup = group;
        return true;
      } else {
        return false;
      }
    }));
    const {
      text: groupText,
      options
    } = selectedGroup;
    content = /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(Breadcrumbs, {
        flush: true,
        margin: "0",
        children: [/*#__PURE__*/_jsx(Link, {
          onClick: () => setSelectedGroupId(null),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "createWorkflow.allTriggersBreadcrumb"
          })
        }), /*#__PURE__*/_jsx("span", {
          children: groupText
        })]
      }), options.map(option => /*#__PURE__*/_jsx(CategoryItem, {
        quickAddState: 'hidden',
        onClick: () => handleSelectTriggerOption(option),
        label: option.text,
        description: option.description,
        additionalContent: isTriggerOptionLocked(option) ? /*#__PURE__*/_jsx(UIIcon, {
          color: FLINT,
          name: "locked"
        }) : null
      }, option.value))]
    });
  } else {
    content = /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(UISearchInput, {
        className: "m-bottom-2",
        value: searchTerm,
        onChange: ({
          target: {
            value
          }
        }) => setSearchTerm(value)
      }), /*#__PURE__*/_jsx(View, {
        marginBlock: "200",
        children: /*#__PURE__*/_jsxs(StyledQuickActionContainer, {
          gap: 12,
          children: [/*#__PURE__*/_jsxs(QuickAction, {
            paddingInline: "100",
            paddingBlock: "200",
            onClick: () => onSelectTriggerOption(manualTriggerOption),
            children: [/*#__PURE__*/_jsx(UIIcon, {
              name: "spacer",
              size: 24,
              className: "m-top-1"
            }), /*#__PURE__*/_jsx(Flex, {
              flexGrow: "1",
              alignItems: "center",
              children: /*#__PURE__*/_jsx(Text, {
                as: "small",
                textAlign: "center",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "createWorkflow.triggerOptions.manual.title"
                })
              })
            })]
          }), /*#__PURE__*/_jsxs(QuickAction, {
            paddingInline: "100",
            paddingBlock: "200",
            onClick: () => onSelectTriggerOption(scheduledTriggerOption),
            children: [/*#__PURE__*/_jsx(UIIcon, {
              name: "time",
              size: 24,
              className: "m-top-1"
            }), /*#__PURE__*/_jsx(Flex, {
              flexGrow: "1",
              alignItems: "center",
              children: /*#__PURE__*/_jsx(Text, {
                as: "small",
                textAlign: "center",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "createWorkflow.triggerOptions.scheduled.title"
                })
              })
            })]
          })]
        })
      }), triggerOptionsLoading || pqlsLoading ? /*#__PURE__*/_jsx(UILoadingSpinner, {
        layout: "centered",
        minHeight: 200
      }) : triggerOptionsSearchResults.map(({
        categoryId,
        text,
        options: groups
      }) => {
        const {
          icon,
          theme,
          descriptionKey
        } = TriggerCategories[categoryId];
        return /*#__PURE__*/_jsx(CategoryAccordion, {
          header: text,
          description: /*#__PURE__*/_jsx(FormattedMessage, {
            message: descriptionKey
          }),
          icon: /*#__PURE__*/_jsx(ActionIcon, {
            name: icon,
            use: theme,
            size: 24
          }),
          hasSearchText: !!searchTerm,
          children: groups.map(({
            text: groupText,
            groupId,
            options
          }, index) => {
            if (groupId === WEBHOOK_EVENTS_GROUP) {
              return null;
            } else if (groupId === '' || searchTerm) {
              return /*#__PURE__*/_jsxs(Fragment, {
                children: [groupText && /*#__PURE__*/_jsx(H7, {
                  className: "m-top-4",
                  children: groupText
                }), options.map(option => /*#__PURE__*/_jsx(CategoryItem, {
                  quickAddState: 'hidden',
                  onClick: () => handleSelectTriggerOption(option),
                  label: option.text,
                  description: option.description,
                  additionalContent: isTriggerOptionLocked(option) ? /*#__PURE__*/_jsx(UIIcon, {
                    color: FLINT,
                    name: "locked"
                  }) : null
                }, option.value))]
              }, groupId || index);
            } else {
              return /*#__PURE__*/_jsx(CategoryItem, {
                quickAddState: 'hidden',
                onClick: () => setSelectedGroupId(groupId),
                label: groupText
              }, groupId);
            }
          })
        }, categoryId);
      }), /*#__PURE__*/_jsx(Button, {
        use: "link",
        onClick: () => onSelectTriggerOption(manualTriggerOption),
        marginBlockStart: "400",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "createWorkflow.skipToManualEnrollment"
        })
      })]
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [CustomModal && /*#__PURE__*/_jsx(CustomModal, {
      onClose: onCloseCustomModal
    }), /*#__PURE__*/_jsx(H5, {
      children: /*#__PURE__*/_jsx(UIHelpIcon, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "createWorkflow.triggerTypeSelectionSubHeader"
        }),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "createWorkflow.triggerTypeSelectionHeader"
        })
      })
    }), content]
  });
}